/**
 * A row's admin actions behind one "more" button. Rendered only for OWNER / ADMIN.
 *
 * The trigger exposes its menu (`aria-haspopup`, `aria-expanded`, `aria-controls`). An action that
 * opens a dialog records the trigger in the screen's `useMenuFocusReturn`, which puts focus back
 * on it once the dialog has closed (DLG-004) — or on the table heading when the row is gone
 * (`useMenuFocusReturn.ts`).
 */
import { useId, useState, type MutableRefObject } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { Icon } from '@/theme/Icon';

export interface RowAction {
  key: string;
  label: string;
  onSelect: () => void;
  /** Disabled while the action's request runs (PERF-006). */
  disabled?: boolean;
}

export function RowActionsMenu({
  label,
  actions,
  opener,
}: {
  label: string;
  actions: RowAction[];
  opener?: MutableRefObject<HTMLElement | null>;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  if (actions.length === 0) return null;
  const open = anchor !== null;
  return (
    <>
      <IconButton
        size="small"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <Icon name="more_vert" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { id: menuId } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={() => {
              if (action.disabled) return;
              if (opener) opener.current = anchor;
              setAnchor(null);
              action.onSelect();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
